import type { StaffTeam } from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Link } from "react-router";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { Badge, pillClass } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { Icon } from "../../ui/icon";
import { PageHeader } from "../../ui/page-header";
import { StatusMessage } from "../../ui/status-message";
import { useTeamCapabilities } from "../access";
import { EndUnavailabilityDialog, UnavailabilityDialog } from "../components/CoverageDialogs";
import { Muted } from "../components/parts";
import { describeTeamError } from "../errors";
import { utcText } from "../format";
import { availabilityReasonLabels, gapKindLabels, gapKindShort, roleLabels } from "../labels";
import { teamKeys, useCoverageGaps, useStaffTeam } from "../queries";

// The Coverage area (CHT-768; supervisors). Where the cover is thin and who is away. Two lists, both from the supported API:
//   - the coverage gaps (a waiting handoff nobody can take, a customer with no primary, a primary who is unavailable with nobody to cover),
//     each with a link to fix the cover of that customer;
//   - the people who cover customers, with their unavailability windows, and the controls to mark a person unavailable or end a window.
// The supervisor's controls are the commands that already existed as an operator script; they are audited with the supervisor and a reason.
//
// The people list is the kit `DataTable`. Its column list is a module constant and the rows carry what a cell needs (see StaffTeamPage).

type Member = StaffTeam["members"][number];
type Window = Member["unavailability"][number];
type DialogState =
  | Readonly<{ kind: "away"; member: Member }>
  | Readonly<{ kind: "end"; member: Member; window: Window }>
  | null;

type PersonView = Readonly<{
  member: Member;
  onAway: () => void;
  onEnd: (window: Window) => void;
}>;

const personColumn = createColumnHelper<PersonView>();
const personColumns = [
  personColumn.display({
    id: "person",
    header: "Person",
    meta: { rowHeader: true },
    cell: ({ row }) => (
      <span className="font-serif text-lg font-medium compact:text-base">
        {row.original.member.displayName}
      </span>
    ),
  }),
  personColumn.display({
    id: "roles",
    header: "Roles",
    cell: ({ row }) => row.original.member.roles.map((role) => roleLabels[role]).join(", "),
  }),
  personColumn.display({
    id: "covers",
    header: "Covers",
    cell: ({ row }) =>
      row.original.member.roles.includes("concierge")
        ? `${row.original.member.activeAssignmentCount} customers`
        : "–",
  }),
  personColumn.display({
    id: "away",
    header: "Not available",
    cell: ({ row }) => {
      const { member, onEnd } = row.original;
      return member.unavailability.length === 0 ? (
        <Muted>Available</Muted>
      ) : (
        <ul className="m-0 grid list-none gap-1 p-0">
          {member.unavailability.map((window) => (
            <li key={window.windowId} className="flex flex-wrap items-center gap-x-2 text-left">
              <span>
                {availabilityReasonLabels[window.reasonCode]}: {utcText(window.startsAt)} to{" "}
                {utcText(window.endsAt)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="font-semibold"
                onClick={() => onEnd(window)}
              >
                End{" "}
                <span className="sr-only">
                  window {window.windowId.slice(0, 8)} of {member.displayName}
                </span>
              </Button>
            </li>
          ))}
        </ul>
      );
    },
  }),
  personColumn.display({
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <Button type="button" variant="outline" size="sm" onClick={row.original.onAway}>
        <Icon name="user-block-01" />
        Mark unavailable <span className="sr-only">{row.original.member.displayName}</span>
      </Button>
    ),
  }),
];

/** The control of a paged list: what is shown, and a button for the next page when the server has more (CHT-768 review fix 6). */
function LoadMore({
  query,
  shown,
  noun,
  label,
  total,
}: Readonly<{
  query: Readonly<{
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => Promise<unknown>;
  }>;
  shown: number;
  noun: string;
  label: string;
  total?: number;
}>) {
  if (!query.hasNextPage && total === undefined) return null;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Muted>
        {total === undefined
          ? `${shown} ${noun} shown. More are waiting.`
          : `${shown} of ${total} ${noun} loaded.`}
      </Muted>
      {query.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => void query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
        >
          {query.isFetchingNextPage ? "Loading…" : label}
        </Button>
      ) : null}
    </div>
  );
}

export function CoveragePage() {
  useWideLayout();
  const { capabilities, isLoading: rolesLoading } = useTeamCapabilities();
  const gaps = useCoverageGaps(capabilities.manageCoverage);
  const team = useStaffTeam(capabilities.manageCoverage);
  const client = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.manageCoverage) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Only a supervisor manages the cover of customers."
      />
    );
  }

  const reload = () => {
    void client.invalidateQueries({ queryKey: teamKeys.team });
    void client.invalidateQueries({ queryKey: teamKeys.gaps });
  };
  const finish = (message: string) => {
    setDialog(null);
    setNotice(message);
    reload();
  };
  const people = (team.data?.members ?? []).filter(
    (member) =>
      member.status === "ACTIVE" &&
      (member.roles.includes("concierge") || member.roles.includes("supervisor")),
  );
  const views: PersonView[] = people.map((member) => ({
    member,
    onAway: () => setDialog({ kind: "away", member }),
    onEnd: (window) => setDialog({ kind: "end", member, window }),
  }));

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title="Coverage"
        titleId="page-title"
        description="Make sure every customer has a concierge who can take the work. Every change needs a reason and is recorded."
      />

      {notice === null ? null : <StatusMessage message={{ tone: "success", text: notice }} />}

      <section aria-labelledby="gaps-title">
        <Card>
          <CardHeader>
            <CardTitle id="gaps-title" className="text-2xl leading-[30px]">
              Coverage gaps
            </CardTitle>
          </CardHeader>
          <CardContent>
            {gaps.isPending ? (
              <AsyncState
                kind="loading"
                title="Loading the gaps"
                message="Waiting for the answer."
              />
            ) : gaps.isError ? (
              <AsyncState
                kind="error"
                title="The gaps could not be loaded"
                message={describeTeamError(gaps.error).message}
                onRetry={() => void gaps.refetch()}
              />
            ) : gaps.data.gaps.length === 0 ? (
              <AsyncState
                kind="empty"
                title="No coverage gap"
                message="Every customer with an open conversation has a concierge who can take the work."
              />
            ) : (
              <ul className="m-0 grid list-none gap-0 p-0" aria-label="Coverage gaps, oldest first">
                {gaps.data.gaps.map((gap) => (
                  <li
                    key={`${gap.kind}-${gap.customer.id}-${gap.handoffId ?? ""}`}
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-hairline py-3"
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                      <strong className="font-serif text-lg font-semibold sm:w-[150px] compact:text-base">
                        {gap.customer.displayName}
                      </strong>
                      <span title={gapKindLabels[gap.kind]} className="inline-flex">
                        <Badge
                          tone="warning"
                          className={cn(pillClass, "justify-center sm:w-[190px]")}
                        >
                          {gapKindShort[gap.kind]}
                        </Badge>
                      </span>
                      <Muted> since {utcText(gap.since)}</Muted>
                    </div>
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/coverage/customers/${encodeURIComponent(gap.customer.id)}`}>
                        Fix the cover
                        <span className="sr-only"> of {gap.customer.displayName}</span>
                      </Link>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {gaps.data?.gaps.length === 0 ? null : (
              <LoadMore
                query={gaps}
                shown={gaps.data?.gaps.length ?? 0}
                noun="gaps"
                label="Load more gaps"
              />
            )}
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-3" aria-labelledby="people-title">
        <h2 id="people-title" className="m-0 font-serif text-[26px] leading-8 font-medium">
          Who is available
        </h2>
        {team.isPending ? (
          <AsyncState kind="loading" title="Loading the team" message="Waiting for the answer." />
        ) : team.isError ? (
          <AsyncState
            kind="error"
            title="The team could not be loaded"
            message={describeTeamError(team.error).message}
            onRetry={() => void team.refetch()}
          />
        ) : people.length === 0 ? (
          <AsyncState
            kind="empty"
            title="Nobody to show"
            message="No active concierge or supervisor."
          />
        ) : (
          <div aria-busy={team.isFetching}>
            <DataTable<PersonView>
              caption="People who cover customers and their unavailability"
              data={views}
              getRowId={(view) => view.member.staffUserId}
              columns={personColumns}
              banded="strong"
            />
          </div>
        )}
        {team.isSuccess ? (
          <LoadMore
            query={team}
            shown={team.data.members.length}
            noun="people"
            label="Load more people"
            total={team.data.summary.total}
          />
        ) : null}
      </section>

      {dialog?.kind === "away" ? (
        <UnavailabilityDialog
          member={dialog.member}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={finish}
        />
      ) : null}
      {dialog?.kind === "end" ? (
        <EndUnavailabilityDialog
          member={dialog.member}
          window={dialog.window}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={finish}
        />
      ) : null}
    </section>
  );
}
